/**
 * MODULE REGISTRY — Single Source of Truth
 * ==========================================
 * All pages/modules in the application are registered here.
 * Adding a new page? Add it to the appropriate module's `subPages` array below.
 * It will automatically appear in:
 *   - Access Control page (for role-based permission toggling)
 *
 * Each subPage entry needs:
 *   { id: 'unique_snake_case_id', label: 'Display Name', path: '/route-path' }
 */

import {
  LayoutDashboard,
  Phone,
  Headphones,
  Briefcase,
  GraduationCap,
  Users,
  DollarSign,
  BarChart3,
  Building2,
  Settings,
  MessageSquare,
  Monitor,
  Zap,
  Lock,
  UserCog,
  Megaphone,
} from 'lucide-react';

const MODULE_HIERARCHY = [
  {
    id: 'sshr',
    label: 'Self-Service HR',
    icon: UserCog,
    subPages: [
      { id: 'sshr_dashboard', label: 'SSHR Dashboard', path: '/sshr' },
      { id: 'sshr_payslips', label: 'My Payslips', path: '/sshr/payslips' },
      { id: 'sshr_calendar', label: 'My Calendar', path: '/sshr/calendar' },
      { id: 'sshr_announcements', label: 'Announcements', path: '/sshr/announcements' },
      { id: 'sshr_tasks', label: 'Task Manager', path: '/hr/tasks' },
      { id: 'sshr_timesheet', label: 'My Timesheet', path: '/sshr/timesheet' },
      { id: 'my_preferences', label: 'My Preferences (Profile & Theme)', path: '/settings' },
    ],
  },
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: LayoutDashboard,
    subPages: [
      { id: 'main_dashboard', label: 'Main Dashboard', path: '/dashboard' },
      { id: 'detailed_dashboard', label: 'Detailed Dashboard', path: '/dashboard/detailed' },
      { id: 'reports', label: 'Custom Report Builder', path: '/reports' },
      { id: 'productivity_report', label: 'Productivity Report', path: '/reports/productivity' },
      { id: 'productivity_dashboard', label: 'Productivity Dashboard', path: '/productivity' },
      { id: 'report_scheduler', label: 'Report Scheduler', path: '/reports/scheduler' },
      { id: 'forecasting', label: 'Forecasting', path: '/forecasting' },
      { id: 'lead_analysis', label: 'Lead Analysis', path: '/analytics/lead-analysis' },
    ],
  },
  {
    id: 'sales',
    label: 'Sales Module',
    icon: Phone,
    subPages: [
      { id: 'sales_crm', label: 'Sales CRM (Kanban)', path: '/sales' },
      { id: 'sales_dashboard', label: 'Sales Dashboard', path: '/sales/dashboard' },
      { id: 'sales_directory', label: 'Sales Directory', path: '/sales/directory' },
      { id: 'today_followups', label: "Today's Follow-ups", path: '/followups' },
      { id: 'leads_pool', label: 'Leads Pool', path: '/leads/pool' },
      { id: 'approvals', label: 'Approvals', path: '/approvals' },
      { id: 'round_robin', label: 'Round Robin', path: '/round-robin' },
      { id: 'sla_management', label: 'SLA Management', path: '/sla-management' },
      { id: 'transfer_requests', label: 'Transfer Requests', path: '/transfer-requests' },
      { id: 'sales_historical_import', label: 'Historical Import', path: '/sales/historical-import' },
      { id: 'lead_assignment_audit', label: 'Lead Assignment Audit', path: '/sales/assignment-audit' },
      { id: 'dormant_leads', label: 'Dormant Leads', path: '/sales/dormant-leads' },
      { id: 'returning_leads', label: 'Returning Leads', path: '/sales/returning-leads' },
    ],
  },
  {
    id: 'customer_service',
    label: 'Customer Service',
    icon: Headphones,
    subPages: [
      { id: 'cs_kanban', label: 'CS Kanban', path: '/cs' },
      { id: 'cs_dashboard', label: 'CS Dashboard', path: '/cs/dashboard' },
      { id: 'customer_master', label: 'Customer Master', path: '/customers' },
      { id: 'student_portal', label: 'Student Portal', path: '/cs/student-portal' },
      { id: 'cs_merge_approvals', label: 'Merge Approvals', path: '/cs/merge-approvals' },
      { id: 'cs_directory', label: 'Student Directory', path: '/cs/directory' },
      { id: 'cs_historical_import', label: 'CS Historical Import', path: '/cs/historical-import' },
      { id: 'shared_call_log', label: 'Shared Call Log', path: '/call-log' },
    ],
  },
  {
    id: 'bd',
    label: 'Business Development',
    icon: Briefcase,
    subPages: [
      { id: 'bd_crm', label: 'BD CRM', path: '/bd' },
      { id: 'bd_dashboard', label: 'BD Dashboard', path: '/bd/dashboard' },
      { id: 'bd_calculator', label: 'BD Calculator', path: '/bd/calculator' },
    ],
  },
  {
    id: 'mentor',
    label: 'Mentor / Academics',
    icon: GraduationCap,
    subPages: [
      { id: 'mentor_crm', label: 'Mentor CRM', path: '/mentor' },
      { id: 'mentor_dashboard', label: 'Mentor Dashboard', path: '/mentor/dashboard' },
      { id: 'leaderboard', label: 'Leaderboard', path: '/mentor/leaderboard' },
      { id: 'cross_deposits', label: 'Cross-Mentor Deposits', path: '/mentor/cross-deposits' },
      { id: 'mentor_historical_import', label: 'Historical Import', path: '/mentor/historical-import' },
    ],
  },
  {
    id: 'hr',
    label: 'HR Module',
    icon: Users,
    subPages: [
      { id: 'hr_dashboard', label: 'HR Dashboard', path: '/hr/dashboard' },
      { id: 'employee_master', label: 'Employee Master', path: '/hr/employees' },
      { id: 'whos_in', label: "Who's In", path: '/hr/whos-in' },
      { id: 'leave_management', label: 'Leave Management', path: '/hr/leave' },
      { id: 'attendance', label: 'Attendance', path: '/hr/attendance' },
      { id: 'attendance_settings', label: 'Attendance Settings', path: '/hr/attendance-settings' },
      { id: 'biocloud_sync', label: 'BioCloud Sync', path: '/hr/biocloud' },
      { id: 'payroll', label: 'Payroll', path: '/hr/payroll' },
      { id: 'performance', label: 'Performance', path: '/hr/performance' },
      { id: 'hr_assets', label: 'Assets', path: '/hr/assets' },
      { id: 'hr_analytics', label: 'HR Analytics', path: '/hr/analytics' },
      { id: 'hr_documents', label: 'Company Documents', path: '/hr/documents' },
      { id: 'hr_approvals', label: 'Approval Queue', path: '/hr/approvals' },
      { id: 'hr_reports', label: 'HR Reports', path: '/hr/reports' },
      { id: 'employee_value', label: 'Employee Value Score', path: '/hr/employee-value' },
      { id: 'attendance_gap', label: 'Attendance Gap', path: '/hr/attendance-gap' },
      { id: 'bulk_attendance', label: 'Bulk Attendance', path: '/hr/bulk-attendance' },
      { id: 'regularization', label: 'Applied Regularization', path: '/hr/regularization' },
      { id: 'regularization_approvals', label: 'Regularization Approvals', path: '/hr/regularization-approvals' },
      { id: 'leave_balance', label: 'Leave Balance', path: '/hr/leave-balance' },
      { id: 'leave_approvals', label: 'Leave Approvals', path: '/hr/leave-approvals' },
      { id: 'timesheet', label: 'Timesheet', path: '/hr/timesheet' },
      { id: 'shift_master', label: 'Shift Master', path: '/hr/shift-master' },
      { id: 'shift_assignment', label: 'Employee Shift Assignment', path: '/hr/shift-assignment' },
      { id: 'late_rules', label: 'Late Rules', path: '/hr/late-rules' },
      { id: 'mentor_roster', label: 'Mentor Roster', path: '/hr/mentor-roster' },
      { id: 'wfh_verification', label: 'WFH Verification', path: '/hr/wfh-verification' },
      { id: 'approval_pipeline', label: 'Approval Pipeline', path: '/hr/approval-pipeline' },
      { id: 'department_performance', label: 'Department Performance', path: '/hr/department-performance' },
      { id: 'offboarding', label: 'Offboarding', path: '/hr/offboarding' },
      { id: 'payroll_pre_run', label: 'Payroll Pre-Run', path: '/hr/payroll/pre-run' },
      { id: 'timesheet_team', label: 'Team Timesheet', path: '/hr/timesheet-team' },
    ],
  },
  {
    id: 'finance',
    label: 'Finance Module',
    icon: DollarSign,
    subPages: [
      { id: 'finance_selector', label: 'Entity Selector', path: '/finance' },
      { id: 'commission_engine', label: 'Commission Engine', path: '/commissions' },
      { id: 'commission_dashboard', label: 'Commission Dashboard', path: '/commission-dashboard' },
      // CLT Finance
      { id: 'clt_dashboard', label: 'CLT Dashboard', path: '/finance/clt/dashboard' },
      { id: 'clt_payables', label: 'CLT Payables', path: '/finance/clt/payables' },
      { id: 'clt_receivables', label: 'CLT Receivables', path: '/finance/clt/receivables' },
      { id: 'finance_verifications', label: 'Payment Verifications', path: '/finance/clt/verifications' },
      { id: 'pending_settlements', label: 'Pending Settlements', path: '/finance/clt/pending-settlements' },
      { id: 'journal_entries', label: 'Journal Entries', path: '/finance/clt/journal' },
      { id: 'finance_settlements', label: 'Settlements', path: '/finance/clt/settlements' },
      { id: 'finance_expenses', label: 'Expenses', path: '/finance/clt/expenses' },
      { id: 'finance_transfers', label: 'Transfers', path: '/finance/clt/transfers' },
      { id: 'finance_accounts', label: 'Accounts', path: '/finance/clt/accounts' },
      { id: 'finance_commission_engine', label: 'Finance Commission Engine', path: '/finance/clt/commission-engine' },
      { id: 'finance_commission_settlements', label: 'Commission Settlements', path: '/finance/clt/commission-settlements' },
      { id: 'finance_reconciliation', label: 'Reconciliation', path: '/finance/clt/reconciliation' },
      // Treasury
      { id: 'treasury_dashboard', label: 'Treasury Dashboard', path: '/finance/treasury/dashboard' },
      { id: 'treasury_balances', label: 'Treasury Balances', path: '/finance/treasury/balances' },
      // Budgeting & PNL
      { id: 'budget_sheet', label: 'Budget Sheet', path: '/finance/budgeting/sheet' },
      { id: 'pnl_dashboard', label: 'PNL Dashboard', path: '/finance/pnl/dashboard' },
      { id: 'data_management', label: 'Data Management', path: '/finance/data/management' },
      // Finance Settings
      { id: 'chart_of_accounts', label: 'Chart of Accounts', path: '/finance/settings/chart-of-accounts' },
      { id: 'bank_accounts', label: 'Bank Accounts', path: '/finance/settings/bank-accounts' },
      { id: 'cost_centers', label: 'Cost Centers', path: '/finance/settings/cost-centers' },
      { id: 'payment_methods', label: 'Payment Methods', path: '/finance/settings/payment-methods' },
      { id: 'payment_gateways', label: 'Payment Gateways', path: '/finance/settings/payment-gateways' },
      { id: 'psp_mapping', label: 'PSP Bank Mapping', path: '/finance/settings/psp-mapping' },
      // Standalone
      { id: 'mentor_withdrawals', label: 'Mentor Withdrawals', path: '/finance/mentor-withdrawals' },
      { id: 'finance_vendors', label: 'Vendor Management', path: '/finance/clt/vendors' },
      { id: 'finance_transactions', label: 'Unified Transactions', path: '/finance/clt/transactions' },
    ],
  },
  {
    id: 'marketing',
    label: 'Marketing Module',
    icon: Megaphone,
    subPages: [
      { id: 'marketing_dashboard', label: 'Analytics Dashboard', path: '/marketing/dashboard' },
      { id: 'lead_connectors', label: 'Lead Connectors', path: '/marketing/connectors' },
      { id: 'marketing_settings', label: 'Marketing Settings', path: '/marketing/settings' },
      { id: 'meta_hub', label: 'Meta Hub', path: '/meta-hub' },
      { id: 'market_analysis', label: 'Market Analysis', path: '/marketing/analysis' },
      { id: 'content_studio', label: 'Content Studio', path: '/marketing/content-studio' },
      { id: 'marketing_calendar', label: 'Content Calendar', path: '/marketing/calendar' },
      { id: 'competitor_intelligence', label: 'Competitor Intelligence', path: '/competitor-intelligence' },
      { id: 'marketing_toolkit', label: 'Marketing Toolkit', path: '/marketing/toolkit' },
      { id: 'marketing_home', label: 'Marketing Home', path: '/marketing' },
    ],
  },
  {
    id: 'operations',
    label: 'Operations',
    icon: Building2,
    subPages: [
      { id: 'qc_dashboard', label: 'QC Dashboard', path: '/qc-dashboard' },
      { id: 'certificates', label: 'Certificate Creation', path: '/certificates' },
      { id: 'duplicate_manager', label: 'Duplicate Manager', path: '/operations/duplicate-manager' },
      { id: 'mt5_sync', label: 'MT5 Integration', path: '/mt5' },
      { id: 'realtime_assist', label: 'Real-Time Assist', path: '/realtime-assist' },
    ],
  },
  {
    id: 'communication',
    label: 'Communication',
    icon: MessageSquare,
    subPages: [
      { id: 'team_chat', label: 'Team Chat', path: '/chat' },
      { id: 'knowledge_base', label: 'Knowledge Base', path: '/knowledge-base' },
      { id: 'chat_monitor', label: 'Chat Monitor', path: '/chat-monitor' },
    ],
  },
  {
    id: 'executive',
    label: 'Executive',
    icon: Zap,
    subPages: [
      { id: 'executive_dashboard', label: 'Executive Dashboard', path: '/executive' },
      { id: 'organization_map', label: 'Organization Map', path: '/organization' },
      { id: 'people_intelligence', label: 'People Intelligence', path: '/people-intelligence' },
    ],
  },
  {
    id: 'it_assets',
    label: 'IT & Assets',
    icon: Monitor,
    subPages: [
      { id: 'it_assets_page', label: 'IT Assets', path: '/it/assets' },
      { id: 'it_asset_management', label: 'Asset Management', path: '/it/asset-management' },
      { id: 'saas_management', label: 'SaaS Management', path: '/it/saas' },
    ],
  },
  {
    id: 'settings',
    label: 'Settings & Admin',
    icon: Settings,
    subPages: [
      { id: 'user_management', label: 'User Management', path: '/users' },
      { id: 'access_control', label: 'Access Control', path: '/access-control' },
      { id: 'role_management', label: 'Role Management', path: '/roles' },
      { id: 'teams_management', label: 'Teams Management', path: '/teams' },
      { id: 'departments', label: 'Departments', path: '/departments' },
      { id: 'courses', label: 'Courses', path: '/courses' },
      { id: 'password_resets', label: 'Password Resets', path: '/password-resets' },
      { id: 'audit_log', label: 'Audit Log', path: '/audit-log' },
      { id: 'admin_settings', label: 'Admin Settings', path: '/admin-settings' },
      { id: 'admin_exports', label: 'Admin Exports', path: '/admin-exports' },
      { id: 'developer_guide', label: 'Developer Guide', path: '/developer-guide' },
      { id: 'claret_admin', label: 'Claret Intelligence', path: '/claret/admin' },
      { id: 'claret_config', label: 'Claret Configuration', path: '/claret/config' },
      { id: 'claret_dashboard', label: 'Claret Dashboard', path: '/claret' },
      { id: 'objection_library', label: 'Objection Library', path: '/claret/objections' },
      { id: 'call_analytics', label: 'Call Analytics', path: '/call-analytics' },
      { id: 'call_intelligence', label: 'Call Intelligence', path: '/call-intelligence' },
      { id: 'script_learning', label: 'Script Learning', path: '/script-learning' },
    ],
  },
];

export default MODULE_HIERARCHY;
